<template>
  <section id="lifecycle" class="lifecycle-section">
    <h2 class="section-title">Our Comprehensive Standards Framework</h2>
    <div class="lifecycle-container">
      <div class="circle-wrapper">
        <div class="circle-container">
          <div class="center-circle">
            <img src="../assets/logo.png" alt="Eduval Logo" class="center-logo">
          </div>
          <div 
            v-for="(standard, index) in standards" 
            :key="index" 
            class="standard-item"
            :class="`standard-${index + 1} ${activeStandard === index ? 'active' : ''}`"
            @mouseenter="setActiveStandard(index)"
            @click="setActiveStandard(index)">
            <div class="standard-icon">
              <i :class="standard.icon"></i>
            </div>
            <div class="standard-label">{{ standard.shortTitle }}</div>
          </div>
        </div>
      </div>
      
      <div class="standard-details" v-if="activeStandard !== null">
        <div class="detail-container">
          <h3>{{ standards[activeStandard].title }}</h3>
          <p>{{ standards[activeStandard].description }}</p>
          <button class="learn-more-btn">Learn More <i class="fas fa-long-arrow-alt-right"></i></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CircularLifecycleSection',
  data() {
    return {
      activeStandard: 0,
      rotationInterval: null,
      standards: [
        {
          icon: 'fas fa-clipboard-check',
          shortTitle: 'Assessment Programme',
          title: 'Assessment Programme Standards',
          description: 'Our assessment programme standards ensure that your institution has robust evaluation systems in place to measure student learning outcomes effectively across all programs.'
        },
        {
          icon: 'fas fa-chalkboard-teacher',
          shortTitle: 'Teaching & Learning',
          title: 'Teaching Learning Quality Assurance and Compliance Standards',
          description: 'Establish excellence in teaching methodologies and ensure compliance with educational regulations through our comprehensive quality assurance standards.'
        },
        {
          icon: 'fas fa-users',
          shortTitle: 'Faculty & Research',
          title: 'Faculty Staff and Research Standards',
          description: 'Develop standards for faculty qualifications, performance evaluation, and research productivity to enhance the academic caliber of your institution.'
        },
        {
          icon: 'fas fa-chart-line',
          shortTitle: 'Outcome Assessment',
          title: 'Outcome Assessments and Performance Management Standards',
          description: 'Implement effective mechanisms to evaluate educational outcomes and manage institutional performance through data-driven approaches.'
        },
        {
          icon: 'fas fa-graduation-cap',
          shortTitle: 'Student Resources',
          title: 'Students Institutional and Learning Resources Standards',
          description: 'Ensure students have access to high-quality institutional support and learning resources necessary for academic success and personal development.'
        },
        {
          icon: 'fas fa-sitemap',
          shortTitle: 'Governance',
          title: 'Governance and Strategic Planning Standards',
          description: 'Establish strong governance structures and strategic planning processes to guide institutional growth, sustainability, and alignment with educational missions.'
        }
      ]
    }
  },
  mounted() {
    // Start the auto-rotation when component mounts
    this.startRotation();
    
    this.animateOnScroll();
    window.addEventListener('scroll', this.animateOnScroll);
  },
  beforeUnmount() {
    // Clear the interval when component is unmounted
    clearInterval(this.rotationInterval);
    window.removeEventListener('scroll', this.animateOnScroll);
  },
  methods: {
    setActiveStandard(index) {
      // Stop auto-rotation when user interacts
      clearInterval(this.rotationInterval);
      this.activeStandard = index;
      
      // Restart rotation after 10 seconds of inactivity
      setTimeout(() => {
        this.startRotation();
      }, 10000);
    },
    startRotation() {
      // Clear any existing interval
      clearInterval(this.rotationInterval);
      
      // Set up a new interval
      this.rotationInterval = setInterval(() => {
        this.activeStandard = (this.activeStandard + 1) % this.standards.length;
      }, 5000);
    },
    animateOnScroll() {
      const section = document.querySelector('.lifecycle-section');
      if (!section) return;
      
      const sectionPosition = section.getBoundingClientRect().top;
      const screenPosition = window.innerHeight / 1.2;
      
      if (sectionPosition < screenPosition) {
        section.classList.add('animate');
      }
    }
  }
}
</script>

<style scoped>
.lifecycle-section {
  padding: 2rem 1.5rem 4rem;
  background-color: #fcfaf2;
  background-image: radial-gradient(rgba(209, 192, 110, 0.05) 2px, transparent 2px), 
                    radial-gradient(rgba(209, 192, 110, 0.05) 2px, transparent 2px);
  background-size: 30px 30px;
  background-position: 0 0, 15px 15px;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
}

.lifecycle-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(135deg, rgba(209, 192, 110, 0.15), rgba(224, 211, 148, 0.1));
  border-bottom-left-radius: 50% 20%;
  border-bottom-right-radius: 50% 20%;
  z-index: 0;
}

.lifecycle-section.animate {
  opacity: 1;
  transform: translateY(0);
}

.section-title {
  text-align: center;
  font-size: 2.2rem;
  color: #444;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(to right, #d1c06e, #e0d394);
  border-radius: 3px;
}

.lifecycle-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  max-width: 1100px;
  margin: 0 auto 3rem;
  position: relative;
  gap: 1.5rem;
  z-index: 1;
}

.circle-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  max-width: 500px;
}

.circle-container {
  position: relative;
  width: 500px;
  height: 500px;
  margin-bottom: 0;
  animation: fadeIn 1s ease-out forwards;
  animation-delay: 0.6s;
  opacity: 0;
}

.lifecycle-section.animate .circle-container {
  opacity: 1;
}

.center-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 140px;
  height: 140px;
  border-radius: 50%;
    background: linear-gradient(135deg, #f5f0d8, #f5edc6);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 30px rgba(209, 192, 110, 0.3);
  z-index: 2;
  animation: pulse 3s infinite alternate;
}

.center-logo {
  width: 70%;
  height: auto;
}

.standard-item {
  position: absolute;
  width: 140px;
  height: 140px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  z-index: 1;
  padding: 12px;
}

.standard-item::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: linear-gradient(135deg, #d1c06e, #e0d394);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  transition: all 0.4s ease;
}

.standard-item:hover, .standard-item.active {
  transform: scale(1.15);
  box-shadow: 0 10px 25px rgba(209, 192, 110, 0.25);
}

.standard-item:hover::before, .standard-item.active::before {
  width: 100%;
  height: 100%;
}

/* Positioning each standard item in a circle */
.standard-1 { top: 5%; left: 50%; transform: translateX(-50%); }
.standard-2 { top: 20%; right: 5%; }
.standard-3 { bottom: 20%; right: 5%; }
.standard-4 { bottom: 5%; left: 50%; transform: translateX(-50%); }
.standard-5 { bottom: 20%; left: 5%; }
.standard-6 { top: 20%; left: 5%; }

/* When active, apply the transformation while preserving positioning */
.standard-1.active { transform: translateX(-50%) scale(1.15); }
.standard-4.active { transform: translateX(-50%) scale(1.15); }

.standard-icon {
  font-size: 2rem;
  color: #d1c06e;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
}

.standard-label {
  font-size: 0.8rem;
  color: #555;
  text-align: center;
  transition: all 0.3s ease;
  white-space: wrap;
  line-height: 1.2;
}

.standard-item:hover .standard-icon, .standard-item.active .standard-icon {
  color: #fff;
}

.standard-item:hover .standard-label, .standard-item.active .standard-label {
  color: #fff;
  font-weight: 600;
}

.standard-details {
  flex: 1;
  max-width: 550px;
  margin: 0;
  padding: 1.5rem 2rem;
  border-radius: 10px;
  background-color: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #d1c06e;
  opacity: 0;
  transform: translateY(20px);
  animation: slideUp 0.5s forwards;
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.detail-container {
  max-width: 100%;
}

.detail-container h3 {
  color: #333;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  position: relative;
  display: inline-block;
}

.detail-container h3::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #d1c06e, rgba(209, 192, 110, 0.3));
}

.detail-container p {
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.learn-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0.8rem 1.5rem;
  background: linear-gradient(135deg, #d1c06e, #e0d394);
  color: white;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.3);
}

.learn-more-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 7px 20px rgba(209, 192, 110, 0.4);
}

.learn-more-btn i {
  transition: transform 0.3s ease;
}

.learn-more-btn:hover i {
  transform: translateX(5px);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 20px rgba(209, 192, 110, 0.3);
  }
  100% {
    box-shadow: 0 0 40px rgba(209, 192, 110, 0.6);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Connecting lines between center and standards */
.circle-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 400px;
  border: 2px dashed rgba(209, 192, 110, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 0;
}

@media (max-width: 992px) {
  .lifecycle-container {
    flex-direction: column;
    align-items: center;
  }
  
  .circle-wrapper {
    margin-bottom: 1rem;
  }
  
  .circle-container {
    width: 450px;
    height: 450px;
  }
  
  .circle-container::before {
    width: 350px;
    height: 350px;
  }
  
  .standard-details {
    width: 100%;
    max-width: 550px;
    min-height: 280px;
  }
}

@media (max-width: 768px) {
  .circle-container {
    width: 400px;
    height: 400px;
  }
  
  .circle-container::before {
    width: 320px;
    height: 320px;
  }
  
  .standard-item {
    width: 100px;
    height: 100px;
    padding: 5px;
  }
  
  .center-circle {
    width: 110px;
    height: 110px;
  }
  
  .standard-details {
    padding: 1.5rem;
    min-height: 280px;
  }
  
  .standard-icon {
    font-size: 1.5rem;
  }
  
  .standard-label {
    font-size: 0.7rem;
  }
}
</style>